import { Link, useParams } from 'react-router-dom';
import { Package, ClipboardCheck, Activity, Pencil } from 'lucide-react';
import { Container } from '@vnetwork/ui/layout';
import { DetailHeader } from '@/components/detail-header';
import {
  Button,
  Card,
  CardHeader,
  CardTitle,
  CardBody,
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
} from '@vnetwork/ui/primitives';
import { useProcess } from '@/api/capacities';
import { InfoGrid, type InfoItem } from '@/pages/account/components/InfoGrid';
import { TabError, TabLoading } from '@/pages/customers/detail/tab-states';
import { formatWhDateTime } from '../shared/wh-format';
import { endpointName, processStatusCell, processTypeCell } from './process-format';
import { ProcessItemsTab } from './detail/process-items-tab';
import { ProcessApprovalsTab } from './detail/process-approvals-tab';
import { ProcessActivityTab } from './detail/process-activity-tab';

export interface CapacityProcessDetailPageProps {
  /** Process id. Falls back to the `:id` route param when omitted. */
  id?: string;
}

/**
 * Warehouse Process detail — identity header (code + type Chip + status pill)
 * over an info card (reason · route · dates), then tabs: Items (expandable line
 * items), Approvals (2-level tech_lead → storekeeper timeline) and Activity log.
 * Read-only (W4 scope).
 */
export default function CapacityProcessDetailPage({ id: idProp }: CapacityProcessDetailPageProps) {
  const params = useParams();
  const id = idProp ?? params.id ?? '';

  const { data: process, isLoading, isError, refetch } = useProcess(id);

  if (isError) {
    return (
      <Container size="full">
        <TabError title="Failed to load process" onRetry={() => refetch()} />
      </Container>
    );
  }
  if (isLoading || !process) {
    return (
      <Container size="full">
        <TabLoading rows={4} />
      </Container>
    );
  }

  const info: InfoItem[] = [
    { label: 'Process Code', value: process.code || '—' },
    { label: 'Type', value: processTypeCell(process.type) },
    { label: 'Status', value: processStatusCell(process.status) },
    { label: 'From Location', value: endpointName(process.source) || '—' },
    { label: 'To Location', value: endpointName(process.destination) || '—' },
    { label: 'Reason', value: process.reason || '—', full: true },
    { label: 'Created At', value: formatWhDateTime(process.created_at) },
    { label: 'Updated At', value: formatWhDateTime(process.updated_at) },
  ];

  const approvals = process.approvals ?? [];
  // Only draft / pending processes can be edited (legacy parity).
  const canEdit = process.status === 'draft' || process.status === 'pending_approval';

  return (
    <Container size="full">
      <div style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
        <DetailHeader
          parent={[
            { label: 'Capacities & Resources', to: '/capacities' },
            { label: 'Processes', to: '/capacities/processes' },
          ]}
          current={process.code || process.id}
          title={process.code || process.id}
          actions={
            canEdit ? (
              <Button variant="secondary" startIcon={<Pencil size={16} />} asChild>
                <Link to={`/capacities/processes/${id}/edit`}>Edit</Link>
              </Button>
            ) : null
          }
        />

        <Card flat>
          <CardHeader>
            <CardTitle>Process Information</CardTitle>
          </CardHeader>
          <CardBody>
            <InfoGrid items={info} />
          </CardBody>
        </Card>

        <Tabs defaultValue="items">
          <TabsList>
            <TabsTrigger value="items">
              <Package size={16} style={{ marginRight: 'var(--vn-space-1)' }} />
              Items
            </TabsTrigger>
            <TabsTrigger value="approvals">
              <ClipboardCheck size={16} style={{ marginRight: 'var(--vn-space-1)' }} />
              Approval Progress
            </TabsTrigger>
            <TabsTrigger value="activity">
              <Activity size={16} style={{ marginRight: 'var(--vn-space-1)' }} />
              Activity Logs
            </TabsTrigger>
          </TabsList>

          <TabsContent value="items">
            <ProcessItemsTab processId={id} />
          </TabsContent>
          <TabsContent value="approvals">
            <ProcessApprovalsTab processId={id} type={process.type} status={process.status} approvals={approvals} />
          </TabsContent>
          <TabsContent value="activity">
            <ProcessActivityTab id={id} />
          </TabsContent>
        </Tabs>
      </div>
    </Container>
  );
}
